import axios from "axios";
import { CreditCard, Gift, Repeat, Settings, Stamp, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import Kpi from "../comum/Kpi";
import { data, num, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_HUB_OFERTAS } from "../rotas";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const ROTA_CONFIG = "/admin/impulsione/fidelize/cartao-fidelidade";

function Frequencia({ f, aMais, roiAMais }) {
  if (!f || f.antes == null || f.depois == null) {
    return <p className="text-sm text-muted-foreground">Ainda não há compras suficientes antes e depois de ligar o cartão para comparar.</p>;
  }
  const ganho = f.antes > 0 ? Math.round(((f.antes - f.depois) / f.antes) * 100) : null;
  const melhor = ganho != null && ganho > 0;
  return (
    <div className="flex flex-wrap items-end gap-6">
      <div><p className="text-xs text-muted-foreground">Antes do cartão</p><p className="text-2xl font-black tabular-nums">{num(f.antes)} dias</p></div>
      <div><p className="text-xs text-muted-foreground">Depois do cartão</p><p className="text-2xl font-black tabular-nums">{num(f.depois)} dias</p></div>
      {ganho != null && (
        <p className={`text-sm font-semibold ${melhor ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
          {melhor ? `Voltam ${ganho}% mais rápido` : "Ainda sem ganho de frequência"}
        </p>
      )}
      {melhor && aMais != null && (
        <p className="text-sm basis-full">
          Estimativa do que o cartão trouxe <b>a mais</b>: <b className="text-emerald-600 dark:text-emerald-400">{reais(aMais)}</b>
          {roiAMais != null && <> · cada R$ 1 em prêmio trouxe <b>{reais(roiAMais)}</b> a mais</>}
        </p>
      )}
      <p className="text-xs text-muted-foreground basis-full">
        Média de dias entre uma compra e outra dos {f.clientes} clientes com cartão, nos {f.janela_dias} dias antes e nos {f.janela_dias} dias depois de ligar. É o número honesto do cartão: mostra se o cliente passou a voltar mais rápido — parte dele compraria de qualquer jeito.
      </p>
    </div>
  );
}

/** Relatório de Ofertas → Cartão Fidelidade (23/10/2026): o ROI do cartão. */
export default function RelatorioCartaoPage() {
  const navigate = useNavigate();
  const { filtros, mudar } = useFiltros();
  const [d, setD] = useState(null);
  const [todos, setTodos] = useState(false);

  useEffect(() => {
    setD(null);
    axios.get("/api/reports/offers/loyalty-card", { ...auth(), params: { date_from: filtros.de, date_to: filtros.ate } })
      .then((r) => setD(r.data)).catch(() => setD({ erro: true }));
  }, [filtros.de, filtros.ate]);

  const p = d?.premios || {};
  return (
    <div className="p-4 sm:p-6" data-e2e="relatorio-cartao">
      <div className="max-w-6xl mx-auto space-y-5">
        <Cabecalho voltarPara={ROTA_HUB_OFERTAS} voltarRotulo="Relatório de Ofertas" icone={CreditCard}
          corIcone="bg-sky-500/10 text-sky-500" titulo="Cartão Fidelidade"
          subtitulo={d && !d.erro ? `${d.ativo ? "Ligado" : "Desligado"}${d.ativado_em ? ` · desde ${data(d.ativado_em)}` : ""} · ${d.cartoes_abertos} cartões abertos agora` : null}
          direita={<FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} />} />

        {!d && <Carregando kpis={4} graficos={false} frase={false} />}
        {d?.erro && <p className="text-sm text-destructive">Não foi possível carregar o relatório.</p>}
        {d && !d.erro && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Faturamento com carimbo" valor={reais(d.faturamento)} icone={TrendingUp}
                dica={`${d.carimbos} pedidos carimbados`} destaque="bg-emerald-500/5 border-emerald-500/30" />
              <Kpi rotulo="Custo dos prêmios" valor={reais(d.custo_real)} icone={Gift}
                dica={`valor de venda ${reais(d.custo_venda)}`} />
              <Kpi rotulo="Retorno bruto" valor={d.roi != null ? `${num(d.roi)}×` : "—"} icone={TrendingUp}
                dica={d.roi != null ? "faturamento com carimbo ÷ custo dos prêmios" : "sem prêmio usado no período"} />
              <Kpi rotulo="Clientes carimbados" valor={num(d.clientes_carimbados)} icone={Users}
                dica={`${d.cartoes_completos} cartões completos`} />
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <section className="rounded-2xl border bg-card p-5">
                <h2 className="font-semibold flex items-center gap-2"><Repeat className="w-4 h-4 text-sky-500" /> O cliente voltou mais rápido?</h2>
                <div className="mt-3"><Frequencia f={d.frequencia} aMais={d.a_mais} roiAMais={d.roi_a_mais} /></div>
              </section>
              <section className="rounded-2xl border bg-card p-5">
                <h2 className="font-semibold flex items-center gap-2"><Stamp className="w-4 h-4 text-sky-500" /> Carimbos e prêmios no período</h2>
                <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                  {[["Carimbos", d.carimbos], ["Estornados", d.estornados], ["Cartões completos", d.cartoes_completos],
                    ["Prêmios liberados", p.liberados], ["Usados", p.usados], ["A usar", p.a_usar]].map(([r, v]) => (
                    <div key={r} className="rounded-xl bg-muted/40 p-3">
                      <p className="text-xl font-black tabular-nums">{num(v || 0)}</p>
                      <p className="text-[11px] text-muted-foreground">{r}</p>
                    </div>
                  ))}
                </div>
                {(p.vencidos > 0 || p.cancelados > 0) && (
                  <p className="text-xs text-muted-foreground mt-3">{p.vencidos} venceram sem uso · {p.cancelados} cancelados (o pedido do 10º carimbo foi cancelado depois).</p>
                )}
                {d.sem_custo > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{d.sem_custo} prêmio(s) de produto sem ficha técnica: o custo contado foi o preço de venda.</p>
                )}
              </section>
            </div>

            {!!d.por_premio?.length && (
              <section className="rounded-2xl border bg-card p-5">
                <h2 className="font-semibold">Por prêmio</h2>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-2 pr-3 font-medium">Prêmio</th><th className="py-2 px-3 font-medium text-right">Liberados</th>
                      <th className="py-2 px-3 font-medium text-right">Usados</th><th className="py-2 px-3 font-medium text-right">Valor de venda</th>
                      <th className="py-2 pl-3 font-medium text-right">Custo real</th></tr></thead>
                    <tbody>{d.por_premio.map((x) => (
                      <tr key={x.premio} className="border-b last:border-0">
                        <td className="py-2 pr-3 font-medium">{x.premio}</td><td className="py-2 px-3 text-right tabular-nums">{x.liberados}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{x.usados}</td><td className="py-2 px-3 text-right tabular-nums">{reais(x.venda)}</td>
                        <td className="py-2 pl-3 text-right tabular-nums">{reais(x.custo)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </section>
            )}

            <section className="rounded-2xl border bg-card p-5">
              <h2 className="font-semibold">Perto de ganhar</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Clientes com 8 ou 9 carimbos agora — bom público para um Disparo.</p>
              {!d.perto_de_ganhar?.length ? (
                <p className="text-sm text-muted-foreground mt-3">Ninguém em 8/10 ou 9/10 por enquanto.</p>
              ) : (
                <ul className="mt-3 grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
                  {d.perto_de_ganhar.slice(0, todos ? undefined : 12).map((c) => (
                    <li key={c.cliente_id} className="py-2 border-b flex items-center justify-between gap-3 text-sm min-w-0">
                      <span className="min-w-0"><span className="font-medium truncate block">{c.nome || "Cliente"}</span>
                        <span className="text-xs text-muted-foreground">{c.telefone || ""} · último carimbo {data(c.ultimo)}</span></span>
                      <span className="shrink-0 font-black tabular-nums text-sky-600 dark:text-sky-400">{c.carimbos}/10</span>
                    </li>
                  ))}
                </ul>
              )}
              {d.perto_de_ganhar?.length > 12 && (
                <button type="button" onClick={() => setTodos((v) => !v)} className="mt-3 text-sm font-semibold text-primary hover:underline">
                  {todos ? "Mostrar menos" : `Mostrar todos (${d.perto_de_ganhar.length})`}
                </button>
              )}
            </section>

            <button type="button" onClick={() => navigate(ROTA_CONFIG)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              <Settings className="w-4 h-4" /> Configurar o cartão fidelidade
            </button>
          </>
        )}
      </div>
    </div>
  );
}
